import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

import generalReducer from './general/general-slice';
import organizationsReducer from './organization/organization-slice';
import robotsReducer from './robots/robots-slice';
import serviceReducer from './service/service-slice';
import localRobotReducer from './local-robot/local-robot-slice';
import localRobotConfigurationReducer from './local-robot-configuration/local-robot-configuration-slice';
import fleetManagementReducer from './fleet-management/fleet-management-slice';

import { getInitialGeneralState } from './general/general-slice-definitions';
import { getInitialOrganizationState } from './organization/organization-slice-definitions';
import { getInitialRobotsState } from './robots/robots-slice-definitions';
import { getInitialServiceState } from './service/service-slice-definitions';
import { getInitialLocalRobotState } from './local-robot/local-robot-slice-definitions';
import { getInitialLocalRobotConfigurationState } from './local-robot-configuration/local-robot-configuration-slice-definitions';
import { getInitialFleetManagementState } from './fleet-management/fleet-management-slice-definitions';
import { robotWebsocketMiddleware } from './middleware/robot-websocket-middleware';
import { analyticsMiddleware } from './middleware/analytics-middleware';

const rootReducer = combineReducers({
  general: generalReducer,
  organizations: organizationsReducer,
  robots: robotsReducer,
  service: serviceReducer,
  localRobot: localRobotReducer,
  localRobotConfiguration: localRobotConfigurationReducer,
  fleetManagement: fleetManagementReducer,
});

export const getInitialState = (): RootState => {
  return {
    general: { ...getInitialGeneralState() },
    organizations: { ...getInitialOrganizationState() },
    robots: { ...getInitialRobotsState() },
    service: { ...getInitialServiceState() },
    localRobot: { ...getInitialLocalRobotState() },
    localRobotConfiguration: { ...getInitialLocalRobotConfigurationState() },
    fleetManagement: { ...getInitialFleetManagementState() },
  };
};

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(robotWebsocketMiddleware, analyticsMiddleware),
});

export const setupTestStore = (preloadedState: RootState) => {
  return configureStore({
    reducer: {
      general: generalReducer,
      organizations: organizationsReducer,
      robots: robotsReducer,
      service: serviceReducer,
      localRobot: localRobotReducer,
      localRobotConfiguration: localRobotConfigurationReducer,
      fleetManagement: fleetManagementReducer,
    },
    preloadedState,
  });
};

export const getWritableInitialState = (): RootState => {
  return JSON.parse(JSON.stringify(getInitialState()));
};

export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = () => useDispatch<AppDispatch>();

export type RootState = ReturnType<typeof store.getState>;
export type AppStore = ReturnType<typeof setupTestStore>;

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export default store;
